import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, Loader2, Package } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { TemplateType } from '../types';
import { TemplateSelector, TEMPLATES } from './templates';

interface FirmDefaultTemplateSettingsProps {
  accessToken: string;
  firmId: string;
}

export function FirmDefaultTemplateSettings({
  accessToken,
  firmId,
}: FirmDefaultTemplateSettingsProps) {
  const [defaultTemplate, setDefaultTemplate] = useState<TemplateType>('custom');
  const [originalTemplate, setOriginalTemplate] = useState<TemplateType>('custom');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [permissionError, setPermissionError] = useState(false);
  const [success, setSuccess] = useState(false);

  // Load default template
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/default-template`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 403) {
            setPermissionError(true);
            setError(errorData.error || 'Insufficient permissions');
            console.error('Permission error details:', errorData);
          }
          throw new Error(errorData.error || 'Failed to load template');
        }

        const data = await response.json();
        const template = data.defaultTemplateType || 'custom';

        setDefaultTemplate(template);
        setOriginalTemplate(template);
      } catch (err: any) {
        console.error('Template load error:', err);
        if (!permissionError) {
          setError(err.message || 'Failed to load template. Using default.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadTemplate();
  }, [firmId, accessToken]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/default-template`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            defaultTemplateType: defaultTemplate,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 403) {
          setPermissionError(true);
          console.error('Permission error details:', errorData);
        }
        throw new Error(errorData.error || 'Failed to update template');
      }

      setOriginalTemplate(defaultTemplate);
      setSuccess(true);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Update template error:', err);
      setError(err.message || 'Failed to update template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedTemplateConfig = TEMPLATES.find((t) => t.id === defaultTemplate);
  const hasChanges = defaultTemplate !== originalTemplate;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Default ITSM Template</CardTitle>
          <CardDescription>
            Select the default template for new client companies
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Default ITSM Template</CardTitle>
        <CardDescription>
          Select the default template for new client companies. You can override this per
          company.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Success Message */}
        {success && (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Default template updated.
            </AlertDescription>
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Current Template Display */}
        <div className="border rounded-lg p-4 bg-muted/30">
          <div className="flex items-center gap-3 mb-3">
            <Package className="h-5 w-5 text-primary" />
            <div>
              <h3 className="font-semibold">Default Template for New Clients</h3>
              <p className="text-sm text-muted-foreground">
                {selectedTemplateConfig?.name || defaultTemplate}
              </p>
            </div>
          </div>
          {selectedTemplateConfig && (
            <p className="text-sm text-muted-foreground">
              {selectedTemplateConfig.description}
            </p>
          )}
        </div>

        {/* Template Selector */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Select Template</h4>
          <TemplateSelector 
            selectedTemplate={defaultTemplate} 
            onSelect={setDefaultTemplate}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleSave} 
            disabled={saving || !hasChanges}
            className="gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Default Template'
            )}
          </Button>
          {hasChanges && (
            <Button
              variant="outline"
              onClick={() => {
                setDefaultTemplate(originalTemplate);
                setError('');
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          )}
        </div>

        {/* Info Alert */}
        <Alert>
          <Package className="h-4 w-4" />
          <AlertDescription>
            <strong>How Default Templates Work</strong>
            <ul className="mt-2 text-sm space-y-1 list-disc list-inside">
              <li>New client companies will automatically use this template</li>
              <li>You can override the template for individual companies</li>
              <li>Changing this won't affect existing companies</li>
              <li>Each template includes platform-specific features and export options</li>
            </ul>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}